import { useRef } from 'react';
import { Switch, Route } from 'react-router-dom';
import { useMediaQuery, Box } from '@mui/material';
import CompanyUpperBar from '../components/Company/UpperBar';
import MyAdvisors from '../scenes/MyAdvisors';
import SearchAdvisors from '../scenes/SearchAdvisors';
import SavedAdvisors from '../scenes/SavedAdvisors';
import TopMatchesAdvisors from '../scenes/TopMatchesAdvisors';
import { useAccount } from '../contexts/Account';
import ImpersonateUpperBar from '../components/ImpersonationUpperBar';
import CompanyDashboard from '../scenes/CompanyDashboard';
import IndividualPostEdit from '../scenes/IndividualPostEdit';
import { CompanyExtraFeedProvider } from '../contexts/CompanyFeed';
import { PostType } from '../services/utils/types';
import FreeTrialBanner from '../components/Company/FreeTrialBanner';
import ExFreemiumBanner from '../components/Company/ExFreemiumBanner';
import OnboardingBanner from '../components/Company/OnboardingBanner';
import IndividualPost from '../scenes/IndividualPost';
import CompanyRightMenu from '../components/Company/RightMenu/index';
import eventBus, { eventBusValues } from '../eventBus';
import CompanyAccount from '../scenes/CompanyAccount';
import Ideas from '../scenes/Ideas';
import BoardMeetings from '../scenes/BoardMeetings';
import DynamicIFrameScene from '../scenes/DynamicIFrameScene';
import CompanyDashboardV2 from '../scenes/CompanyDashboardV2';
import Treatment from '../components/Treatment';
import { featureFlags } from '../splitSetup';
import MyAdvisorsLegacy from '../scenes/MyAdvisorsLegacy';
import SearchAdvisorsLegacy from '../scenes/SearchAdvisors/SearchAdvisorsLegacy';
import SavedAdvisorsLegacy from '../scenes/SavedAdvisorsLegacy';
import BoardActivities from '../scenes/BoardActivities';
import TopMatchesAdvisorsLegacy from '../scenes/TopMatchesAdvisors/TopMatchesAdvisorsLegacy';

function getTopValue(isSM, showSmallBanner) {
  if (isSM) {
    return '50px';
  }
  if (showSmallBanner) {
    return '50px';
  }
  return '50px';
}

const useStyles = ({ isSM, showSmallBanner }) => ({
  companyContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 'auto',
  },
  viewContainer: {
    marginTop: getTopValue(isSM, showSmallBanner),
    overscrollBehavior: 'contain',
    display: 'flex',
    flex: 'auto',
    overflowX: 'hidden',
    height: 'calc(100vh - 50px)',
    '&::-webkit-scrollbar': {
      width: '8px',
      backgroundColor: '#F8F8F8',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '5px',
      backgroundColor: '#C7C7C7',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#B7B7B7',
    },
    '&::-webkit-scrollbar-thumb:active': {
      backgroundColor: '#B7B7B7',
      boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
    },
  },
  childrenContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    width: '100%',
  },
  companyContainerImpersonated: {
    display: 'flex',
    flexDirection: 'row',
    flex: 'auto',
    justifyContent: 'center',
  },
});

function BaseLayout() {
  const {
    isImpersonated,
    isFreeTrial,
    accountDetails,
    isFreemium,
    currentUserInfo,
  } = useAccount();
  const showExFremiumBanner =
    accountDetails.freemiumRegistration &&
    accountDetails.opportunityStage === 'discovery' &&
    !isFreemium;
  const showOnboardingBanner =
    !isFreeTrial &&
    accountDetails.opportunityStage === 'discovery' &&
    accountDetails.onboardingStatus !== 'Not Requisite' &&
    accountDetails.onboardingStatus !== 'Complete' &&
    !accountDetails.freemiumRegistration;
  const isSM = useMediaQuery((umq) => umq.breakpoints.down('sm'));
  const classes = useStyles({
    isSM,
    showSmallBanner: showExFremiumBanner || showOnboardingBanner,
  });
  const boxRef = useRef();

  return (
    <>
      <CompanyExtraFeedProvider>
        {isImpersonated && <ImpersonateUpperBar />}
        {isFreemium ? null : (
          <FreeTrialBanner show={isFreeTrial} isCompanyContact />
        )}
        {accountDetails.freemiumRegistration &&
          accountDetails.opportunityStage === 'discovery' &&
          !isFreemium && <ExFreemiumBanner show />}
        {showOnboardingBanner && <OnboardingBanner show />}
        <div
          style={
            isImpersonated
              ? classes.companyContainerImpersonated
              : classes.companyContainer
          }
        >
          <CompanyRightMenu
            onDismiss={() => {
              eventBus.dispatch(eventBusValues.mobileCompanyMenuTrigger);
            }}
            accountDetails={accountDetails}
          />

          <div style={classes.childrenContainer}>
            <Treatment
              userId={currentUserInfo?.username || 'not_id'}
              splitNames={featureFlags.company_redesign}
              updateOnSdkTimedout
              evaluatedComponent={<CompanyUpperBar />}
              conditionFlag
              conditionFailedComponent={<CompanyUpperBar legacy />}
            />

            <Box sx={classes.viewContainer} ref={boxRef}>
              <div style={{ width: '100%' }}>
                <Switch>
                  <Route
                    path="/dashboard"
                    render={() => (
                      <Treatment
                        userId={currentUserInfo?.username || 'not_id'}
                        splitNames={featureFlags.company_redesign}
                        updateOnSdkTimedout
                        evaluatedComponent={<CompanyDashboardV2 />}
                        conditionFlag
                        conditionFailedComponent={<CompanyDashboard />}
                      />
                    )}
                  />
                  <Route path="/messages" component={CompanyDashboard} />
                  {!isFreemium && (
                    <Route
                      path="/board-meetings"
                      component={() => <BoardMeetings />}
                    />
                  )}
                  <Route path="/post/:postId?" component={IndividualPost} />
                  <Route
                    path="/question/:postId?/edit"
                    component={() => (
                      <IndividualPostEdit postType={PostType.QUESTION} />
                    )}
                  />
                  <Route exact path="/advisor_ideas" component={Ideas} />
                  <Route
                    path="/board_activity"
                    component={() => <BoardActivities />}
                  />
                  <Route
                    exact
                    path="/advisors"
                    component={() => (
                      <Treatment
                        userId={currentUserInfo?.username || 'not_id'}
                        splitNames={featureFlags.company_redesign}
                        updateOnSdkTimedout
                        evaluatedComponent={<MyAdvisors boxRef={boxRef} />}
                        conditionFlag
                        conditionFailedComponent={
                          <MyAdvisorsLegacy boxRef={boxRef} />
                        }
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/advisors/search"
                    component={() => (
                      <Treatment
                        userId={currentUserInfo?.username || 'not_id'}
                        splitNames={featureFlags.company_redesign}
                        updateOnSdkTimedout
                        evaluatedComponent={<SearchAdvisors boxRef={boxRef} />}
                        conditionFlag
                        conditionFailedComponent={
                          <SearchAdvisorsLegacy boxRef={boxRef} />
                        }
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/advisors/saved"
                    component={() => (
                      <Treatment
                        userId={currentUserInfo?.username || 'not_id'}
                        splitNames={featureFlags.company_redesign}
                        updateOnSdkTimedout
                        evaluatedComponent={<SavedAdvisors boxRef={boxRef} />}
                        conditionFlag
                        conditionFailedComponent={
                          <SavedAdvisorsLegacy boxRef={boxRef} />
                        }
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/advisors/top_matches"
                    component={() => (
                      <Treatment
                        userId={currentUserInfo?.username || 'not_id'}
                        splitNames={featureFlags.company_redesign}
                        updateOnSdkTimedout
                        evaluatedComponent={
                          <TopMatchesAdvisors boxRef={boxRef} />
                        }
                        conditionFlag
                        conditionFailedComponent={
                          <TopMatchesAdvisorsLegacy boxRef={boxRef} />
                        }
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/advisors/:publicProfileUrl"
                    component={MyAdvisors}
                  />
                  <Route exact path="/account" component={CompanyAccount} />
                  <Route path="/ub" component={DynamicIFrameScene} />
                  <Route path="/wf" component={DynamicIFrameScene} />
                </Switch>
              </div>
            </Box>
          </div>
        </div>
      </CompanyExtraFeedProvider>
    </>
  );
}

export default BaseLayout;
